import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { defaultStoreDTO } from '@utils/store';

import { IPage, IPageSize, ISort, TDefaultStoreDTO } from '@interfaces/common/store';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { defaultMeta } from '@enums/common';
import { TSeverity } from '@enums/kpi-common';

interface IInitialState extends TDefaultStoreDTO {
	selectedSeverity: TSelectOption<string> | null;
	status: TSelectOption<string> | null;
	history: TDefaultStoreDTO & {
		severity: TSeverity | null;
		fireTime: number | null;
		wasSent: boolean | null;
	};
}

const initialState: IInitialState = {
	...defaultMeta,
	sortDirection: false,
	sortFieldName: null,
	searchValue: '',
	selectedSeverity: null,
	status: null,
	history: {
		...defaultMeta,
		sortDirection: false,
		sortFieldName: null,
		searchValue: '',
		severity: null,
		fireTime: null,
		wasSent: false,
	},
};

const kpiAlerts = createSlice({
	name: 'kpiAlerts',
	initialState,
	reducers: {
		...defaultStoreDTO,
		setSortDirection: (state, action: PayloadAction<{
			sortDirection: boolean | null;
		}>) => {
			state.sortDirection = action.payload.sortDirection;
		},
		setSortFieldName: (state, action: PayloadAction<{
			sortFieldName: string;
		}>) => {
			state.sortFieldName = action.payload.sortFieldName;
		},
		setSelectedSeverity: (state, action: PayloadAction<{
			selectedSeverity: TSelectOption<string> | null;
		}>) => {
			state.selectedSeverity = action.payload.selectedSeverity;
			state.page = 1;
		},
		setStatus: (state, action: PayloadAction<{
			status: TSelectOption<string> | null;
		}>) => {
			state.status = action.payload.status;
			state.page = 1;
		},
		setHistoryPage: (state, action: PayloadAction<IPage>) => {
			state.history.page = action.payload.page;
		},
		setHistoryPageSize: (state, action: PayloadAction<IPageSize>) => {
			state.history.pageSize = action.payload.pageSize;
			state.history.page = initialState.history.page;
		},
		setHistorySort: (state, action: PayloadAction<ISort>) => {
			state.history.sortDirection = action.payload.sortDirection;
			state.history.sortFieldName = action.payload.sortFieldName;
		},
		setHistorySearchValue: (state, action: PayloadAction<{ searchValue: string }>) => {
			state.history.searchValue = action.payload.searchValue;
			state.history.page = initialState.page;
		},
		setHistorySortDirection: (state, action: PayloadAction<{
			sortDirection: boolean | null;
		}>) => {
			state.history.sortDirection = action.payload.sortDirection;
			state.history.page = initialState.page;
		},
		setHistorySortFieldName: (state, action: PayloadAction<{
			sortFieldName: string | null;
		}>) => {
			state.history.sortFieldName = action.payload.sortFieldName;
			state.history.page = initialState.page;
		},
		setHistorySeverity: (state, action: PayloadAction<{
			severity: TSeverity | null;
		}>) => {
			state.history.severity = action.payload.severity;
			state.history.page = initialState.page;
		},
		setHistoryFireTime: (state, action: PayloadAction<{
			fireTime: number | null;
		}>) => {
			state.history.fireTime = action.payload.fireTime;
			state.history.page = initialState.page;
		},
		setHistoryWasSent: (state, action: PayloadAction<{
			wasSent: boolean | null;
		}>) => {
			state.history.wasSent = action.payload.wasSent;
			state.history.page = initialState.page;
		},
	},
});

export const {
	setPage,
	setPageSize,
	setSort,
	setSearchValue,
	setSelectedSeverity,
	setStatus,
	setHistoryPage,
	setHistoryPageSize,
	setHistorySort,
	setHistorySearchValue,
	setHistoryFireTime,
	setHistorySeverity,
	setHistoryWasSent,
} = kpiAlerts.actions;

export default kpiAlerts.reducer;
